import { useEffect, useState } from "react";
import { Bookmark, Trash2, Play, Repeat2, Loader2, Send, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { deletePhrase, listPhrases, reviewPhrase, speak } from "@/lib/api";
import { canRecord, useVoiceInput } from "@/hooks/useVoiceInput";

export default function MyPhrases() {
  const { tr } = useLang();
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewId, setReviewId] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [revealed, setRevealed] = useState({});
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewAudioUsed, setReviewAudioUsed] = useState(false);
  const voice = useVoiceInput({
    onTranscript: (t) => { setReviewText((p) => (p ? p + " " : "") + t); setReviewAudioUsed(true); },
    onError: (k) => {
      if (k === "denied") toast.error(tr("micDenied"));
      else if (k === "unsupported") toast.info(tr("micUnsupported"));
      else if (k === "provider") toast.error(tr("errorGeneric"));
    },
  });

  const reload = () => {
    listPhrases().then(setPhrases).catch(() => toast.error(tr("errorGeneric"))).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const play = async (text) => {
    try {
      const b = await speak(text, 1.0);
      const url = URL.createObjectURL(b);
      const a = new Audio(url);
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
    } catch { toast.info(tr("ttsUnavailable")); }
  };

  const doDelete = async (id) => {
    try {
      await deletePhrase(id);
      setPhrases((p) => p.filter((x) => x.id !== id));
    } catch (err) { toast.error(tr("errorGeneric")); }
  };

  const startReview = (id) => {
    setReviewId(id);
    setReviewText("");
    setReviewResult(null);
    setRevealed((r) => ({ ...r, [id]: false }));
  };

  const submitReview = async () => {
    if (!reviewId || !reviewText.trim()) return;
    setReviewSubmitting(true);
    // Local exact-match check: if the learner produces the saved English
    // phrase verbatim (case/whitespace insensitive), acknowledge it as
    // correct BEFORE any AI more-natural alternatives. Server evaluation
    // still runs but any suggestion is framed as optional.
    const target = (phrases.find((x) => x.id === reviewId)?.english || "").trim().toLowerCase();
    const attempt = reviewText.trim().toLowerCase();
    const exactMatch = target && attempt === target;
    try {
      const r = await reviewPhrase(reviewId, reviewText.trim(), reviewAudioUsed);
      setReviewResult({ ...r, exact_match: exactMatch });
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-10 space-y-4" data-testid="phrases-screen">
        <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="phrases-title">
          {tr("phrasesTitle")}
        </h1>

        {!loading && phrases.length === 0 && (
          <div className="wpe-card text-[13px] text-[rgb(var(--wpe-muted))]" data-testid="phrases-empty">
            {tr("phrasesEmpty")}
          </div>
        )}

        {phrases.map((p, idx) => {
          const cardClasses = ["wpe-card-mint", "wpe-card-coral", "wpe-card-gold", "wpe-card-blue", "wpe-card-lavender"];
          return (
          <div key={p.id} className={`wpe-card ${cardClasses[idx % cardClasses.length]} space-y-2`} data-testid={`phrase-${p.id}`}>
            <div className="flex items-start gap-2">
              <Bookmark size={14} className="text-[rgb(var(--wpe-teal))] mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                {reviewId === p.id && !revealed[p.id] ? (
                  <button
                    type="button"
                    onClick={() => setRevealed((r) => ({ ...r, [p.id]: true }))}
                    className="text-[13px] font-semibold text-[rgb(var(--wpe-teal-hover))] underline decoration-dotted"
                    data-testid={`phrase-reveal-${p.id}`}
                  >
                    {tr("phraseRevealCta")}
                  </button>
                ) : (
                  <div className="text-[14px] text-[rgb(var(--wpe-navy))] font-medium">{p.english}</div>
                )}
                {p.translation_es ? (
                  <div className="text-[12px] text-[rgb(var(--wpe-slate))]" data-testid={`phrase-translation-${p.id}`}>
                    <span className="uppercase tracking-wider text-[10px] mr-1">{tr("phraseTranslation")}:</span>
                    {p.translation_es}
                  </div>
                ) : (
                  <div className="text-[12px] text-[rgb(var(--wpe-muted))] italic" data-testid={`phrase-translation-missing-${p.id}`}>
                    {tr("phraseTranslationMissing")}
                  </div>
                )}
                {p.spanish && (
                  <div className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid={`phrase-cue-${p.id}`}>
                    <span className="uppercase tracking-wider text-[10px] mr-1">{tr("phraseCoachingCue")}:</span>
                    {p.spanish}
                  </div>
                )}
                <div className="text-[10px] text-[rgb(var(--wpe-muted))] mt-1 uppercase tracking-wider">
                  {p.source_type}{p.source_scenario_id ? ` · ${p.source_scenario_id}` : ""}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => play(p.english)} className="wpe-ghost text-[11px]" data-testid={`phrase-play-${p.id}`}>
                <Play size={12} /> {tr("listen")}
              </button>
              <button type="button" onClick={() => startReview(p.id)} className="wpe-ghost text-[11px]" data-testid={`phrase-review-${p.id}`}>
                <Repeat2 size={12} /> {tr("reviewCta")}
              </button>
              <button type="button" onClick={() => doDelete(p.id)} className="wpe-ghost text-[11px]" data-testid={`phrase-delete-${p.id}`}>
                <Trash2 size={12} /> {tr("deletePractice")}
              </button>
            </div>

            {reviewId === p.id && (
              <div className="border-t border-[rgb(var(--wpe-border))] pt-2 mt-2 space-y-2" data-testid={`review-panel-${p.id}`}>
                <div className="wpe-eyebrow">{tr("reviewPromptEs")}</div>
                <div className="text-[13px] text-[rgb(var(--wpe-navy))]" data-testid={`review-prompt-${p.id}`}>
                  {p.translation_es || p.spanish || "—"}
                </div>
                <textarea
                  className="wpe-input min-h-[60px] resize-none text-[13px]"
                  placeholder={tr("typeYourAnswer")}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value.slice(0, 800))}
                  data-testid={`review-input-${p.id}`}
                />
                {canRecord() && (
                  <button
                    type="button"
                    onClick={voice.recording ? voice.stop : voice.start}
                    disabled={voice.transcribing}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-[12px] font-semibold ${voice.recording ? "bg-[rgb(var(--wpe-danger))] text-white" : "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"}`}
                    data-testid={`review-mic-${p.id}`}
                  >
                    {voice.recording ? <Square size={12} /> : <Mic size={12} />}
                    {voice.recording ? tr("stop") : (voice.transcribing ? tr("transcribing") : tr("micTap"))}
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={submitReview}
                    disabled={reviewSubmitting || !reviewText.trim()}
                    className="wpe-cta text-[12px] flex-1"
                    data-testid={`review-submit-${p.id}`}
                  >
                    {reviewSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {tr("submitReview")}
                  </button>
                </div>
                {reviewResult && (
                  <div className="space-y-1" data-testid={`review-result-${p.id}`}>
                    {reviewResult.exact_match && (
                      <div className="wpe-hint text-[12px] wpe-card-mint" data-testid={`review-correct-${p.id}`}>
                        {tr("reviewCorrectAck")}
                      </div>
                    )}
                    <div className="wpe-hint text-[12px]">
                      <div className="wpe-eyebrow mb-1">
                        {reviewResult.exact_match ? tr("feedbackAlternativesOptional") : tr("coachNote")}
                      </div>
                      {reviewResult.coach_note_es}
                    </div>
                    <div className="text-[12px]">
                      <span className="wpe-eyebrow">{tr("revealAnswer")}: </span>
                      <span className="text-[rgb(var(--wpe-navy))] font-medium">{reviewResult.model_answer_en}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
